/**
 * Pure maths for the conversion rate calculator, kept out of the widget so the
 * numbers on the page can be checked without rendering anything.
 *
 * Rates are returned as fractions (0.04 = 4%). Functions return NaN instead of
 * throwing when the inputs cannot produce a number.
 */

/** z for a two-sided 95% interval. */
const Z95 = 1.959963984540054

/**
 * Below this many conversions the 95% range on the rate spans roughly a
 * factor of two or more (30 of 600 → 3.5%–7.0%), which is too wide to rank
 * one ad, page or audience above another.
 */
export const SMALL_SAMPLE_CONVERSIONS = 30

/** Conversion rate = conversions ÷ clicks (or sessions). */
export function conversionRate(conversions: number, denominator: number): number {
  if (!(conversions >= 0) || !(denominator > 0)) return NaN
  return conversions / denominator
}

/** Cost per conversion = spend ÷ conversions. */
export function costPerConversion(spend: number, conversions: number): number {
  if (!(spend >= 0) || !(conversions > 0)) return NaN
  return spend / conversions
}

/**
 * Wilson score interval for a proportion — the range of true conversion rates
 * consistent with the observed counts. Unlike the textbook p ± z·√(p(1−p)/n)
 * it stays inside 0–100% and does not collapse to a zero-width range at
 * 0 conversions. Only meaningful when conversions ≤ denominator.
 */
export function wilsonInterval(
  conversions: number,
  denominator: number,
  z: number = Z95,
): { low: number; high: number } {
  if (!(conversions >= 0) || !(denominator > 0) || conversions > denominator) {
    return { low: NaN, high: NaN }
  }
  const n = denominator
  const p = conversions / n
  const z2 = z * z
  const centre = (p + z2 / (2 * n)) / (1 + z2 / n)
  const half =
    (z * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))) / (1 + z2 / n)
  return { low: Math.max(0, centre - half), high: Math.min(1, centre + half) }
}
